import { useTRPC } from "../../trpc";
import { FormattedMessage, useIntl } from "react-intl";
import { useCallback, useMemo, useState } from "react";
import { Button, Group, Stack } from "@mantine/core";
import { SimpleBreadcrumbs } from "../../components/SimpleBreadcrumbs";
import { useQuery } from "@tanstack/react-query";
import { AgGridTable } from "../../components/AgGridTable.js";
import { useDisclosure } from "@mantine/hooks";
import { AddAccountModal } from "./components/AddAccountModal";
import { EditAccountModal } from "./components/EditAccountModal";
import type { RouterOutputs } from "@project/api";
import type { ColDef, RowSelectedEvent } from "ag-grid-community";

export type Account = RouterOutputs["getAccounts"][number];

const autoSizeStrategy = {
  type: "fitGridWidth",
  defaultMinWidth: 100,
} as const;

const rowSelection = {
  mode: "singleRow",
  enableClickSelection: true,
} as const;

const defaultColDef = {
  sortable: true,
  filter: true,
  resizable: true,
};

export function Accounts() {
  const intl = useIntl();
  const trpc = useTRPC();
  const [selected, setSelected] = useState<Account | null>(null);
  const { data = [] } = useQuery(trpc.getAccounts.queryOptions());

  const [
    addAccountModalOpened,
    {
      open: openAddAccountModal,
      close: closeAddAccountModal,
    }] = useDisclosure(false);

  const [
    editAccountModalOpened,
    {
      open: openEditAccountModal,
      close: closeEditAccountModal,
    }] = useDisclosure(false);

  const handleRowSelection = useCallback((event: RowSelectedEvent<Account>) => {
    if (!event.event) {
      return;
    }
    const isSelected = event.node.isSelected();
    const rowData = event.node.data;

    if (isSelected && rowData) {
      setSelected(rowData);
    }
    else {
      setSelected(null);
    }
  }, []);

  const columnDefs: ColDef<Account>[] = useMemo(() => [
    {
      field: "id",
      headerName: intl.formatMessage({ defaultMessage: "ID", id: "column.id" }),
    },
    {
      field: "name",
      headerName: intl.formatMessage({ defaultMessage: "Name", id: "column.name" }),
    },
    {
      field: "isDevelopmentPartner",
      headerName: intl.formatMessage({ defaultMessage: "Dev", id: "column.isDevelopmentPartner" }),
      width: 100,
    },
    {
      field: "createdAt",
      headerName: intl.formatMessage({ defaultMessage: "Created at", id: "column.createdAt" }),
      sortable: true,
      type: "dateTime",
    },
    {
      field: "deletedAt",
      headerName: intl.formatMessage({ defaultMessage: "Status", id: "column.status" }),
      valueGetter: params => params.data?.deletedAt
        ? "inactive"
        : "active",
    },
  ], [intl]);

  return (
    <Stack gap={16} h="100%">
      <SimpleBreadcrumbs
        parent={intl.formatMessage({ defaultMessage: "Admin", id: "navigation.admin" })}
        current={intl.formatMessage({ defaultMessage: "Accounts", id: "navigation.accounts" })}
        description={intl.formatMessage({ defaultMessage: "See and manage customer accounts", id: "text.accountsViewDescription" })}
      />
      <Group justify="space-between" mt="md">
        <Group>
          {selected && (
            <Button size="xs" onClick={openEditAccountModal} variant="primary">
              <FormattedMessage
                defaultMessage="Edit"
                id="button.edit"
              />
            </Button>
          )}
        </Group>
        <Group>
          <Button size="xs" onClick={openAddAccountModal} variant="primary">
            <FormattedMessage defaultMessage="Create new" id="button.createNew" />
          </Button>
        </Group>
      </Group>
      <AgGridTable
        rowData={data}
        rowSelection={rowSelection}
        onRowSelected={handleRowSelection}
        columnDefs={columnDefs}
        domLayout="autoHeight"
        autoSizeStrategy={autoSizeStrategy}
        suppressHorizontalScroll={false}
        defaultColDef={defaultColDef}
      />
      <AddAccountModal opened={addAccountModalOpened} onClose={closeAddAccountModal} />
      {selected && (
        <EditAccountModal
          account={selected}
          opened={editAccountModalOpened}
          onClose={closeEditAccountModal}
        />
      )}
    </Stack>
  );
}
